import { todayIso, type AssistantDraft } from '@fin/shared';
import { useEffect, useRef, useState } from 'react';
import { ApiError, api, post } from '../../lib/api.ts';
import { categoryTree, useCategories } from '../../lib/data.ts';
import { priorityLabels } from '../../lib/format.ts';
import { invalidate } from '../../lib/query.ts';
import { listen, speechSupported } from '../../lib/speech.ts';
import { Field, Icon, Input, NumberInput, Segmented, Select, cx, useIsMobile } from '../../components/ui.tsx';

const MAX = 300;

/** what the words sheet adds: it follows the recognized request and can be switched by hand */
export type Target = 'operation' | 'wishlist' | 'event' | 'category';
export const targetOf: Partial<Record<AssistantDraft['intent'], Target>> = {
  add_expense: 'operation', add_income: 'operation', transfer: 'operation', add_debt: 'operation',
  add_wishlist: 'wishlist', add_event: 'event', add_category: 'category',
};
export const targetTitles: Record<Target, string> = {
  operation: 'Новая операция', wishlist: 'Новая покупка', event: 'Новое событие', category: 'Новая категория',
};
export const targetActions: Record<Exclude<Target, 'operation'>, string> = {
  wishlist: 'Добавить покупку', event: 'Добавить событие', category: 'Добавить категорию',
};

export interface OtherEntry { name: string; amount: number | null; date: string; priority: number; kind: 'expense' | 'income'; parentId: number | '' }
const emptyEntry = (): OtherEntry => ({ name: '', amount: null, date: todayIso(), priority: 3, kind: 'expense', parentId: '' });
const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * A purchase, an event or a category said in words. Like the operation form, a new parse changes only what was
 * said anew; `fresh` («Распознать заново») starts the entry over from the phrase.
 */
export function useOtherEntry() {
  const [entry, setEntry] = useState<OtherEntry>(emptyEntry);
  const heard = useRef<Partial<AssistantDraft>>({});
  const apply = (d: AssistantDraft, fresh: boolean) => {
    if (fresh) heard.current = {};
    const h = heard.current;
    const next: Partial<OtherEntry> = {};
    const name = d.intent === 'add_category' ? d.name : d.note;
    if (name && name !== (d.intent === 'add_category' ? h.name : h.note)) next.name = capital(name);
    if (d.amount && d.amount !== h.amount) next.amount = d.amount;
    if (d.intent === 'add_event' && d.date && d.date !== h.date) next.date = d.date;
    if (d.kind && d.kind !== h.kind) next.kind = d.kind;
    if (d.parentId && d.parentId !== h.parentId) next.parentId = d.parentId;
    heard.current = { ...h, ...Object.fromEntries(Object.entries(d).filter(([, v]) => v !== null)) };
    setEntry((x) => ({ ...(fresh ? emptyEntry() : x), ...next }));
  };
  const forget = () => { heard.current = {}; };
  const set = (p: Partial<OtherEntry>) => setEntry((x) => ({ ...x, ...p }));
  const save = async (target: Exclude<Target, 'operation'>) => {
    const name = entry.name.trim();
    if (!name) throw new Error(target === 'event' ? 'Назовите событие' : 'Укажите название');
    if (target === 'wishlist') {
      await post('/wishlist', { name, price: entry.amount, priority: entry.priority, currency: 'RUB' });
      invalidate('/wishlist');
      return 'Покупка добавлена в список';
    }
    if (target === 'event') {
      await post('/events', { title: name, date: entry.date, amount: entry.amount ?? 0, currency: 'RUB' });
      invalidate('/events', '/dashboard');
      return 'Событие добавлено';
    }
    await post('/categories', { name, kind: entry.kind, parentId: entry.parentId || null });
    invalidate('/categories');
    return 'Категория добавлена';
  };
  return { entry, set, apply, forget, save };
}

/** the fields of a purchase, an event or a category in the words sheet */
export function OtherFields({ target, entry, set }: { target: Exclude<Target, 'operation'>; entry: OtherEntry; set: (p: Partial<OtherEntry>) => void }) {
  const cats = useCategories();
  const parents = categoryTree((cats.data ?? []).filter((c) => c.kind === entry.kind && !c.archived)).map((n) => n.cat);
  if (target === 'category') {
    return (
      <div className="form">
        <Field label="Название"><Input value={entry.name} onChange={(e) => set({ name: e.target.value })} /></Field>
        <Segmented wide label="Тип категории" value={entry.kind} onChange={(kind) => set({ kind, parentId: '' })}
          options={[{ value: 'expense', label: 'Расход' }, { value: 'income', label: 'Доход' }]} />
        <Field label="Где">
          <Select value={entry.parentId} onChange={(e) => set({ parentId: e.target.value ? Number(e.target.value) : '' })}>
            <option value="">Отдельная категория</option>
            {parents.map((c) => <option key={c.id} value={c.id}>внутри «{c.name}»</option>)}
          </Select>
        </Field>
      </div>
    );
  }
  return (
    <div className="form">
      <Field label={target === 'event' ? 'Событие' : 'Что купить'}><Input value={entry.name} onChange={(e) => set({ name: e.target.value })} /></Field>
      <div className="form-row">
        {target === 'event' ? (
          <Field label="Дата" className="grow"><Input type="date" value={entry.date} onChange={(e) => set({ date: e.target.value })} /></Field>
        ) : null}
        <Field label={target === 'event' ? 'Сумма' : 'Цена'} className="grow"><NumberInput value={entry.amount} onChange={(amount) => set({ amount })} /></Field>
        {target === 'wishlist' ? (
          <Field label="Приоритет" className="grow">
            <Select value={entry.priority} onChange={(e) => set({ priority: Number(e.target.value) })}>
              {[1, 2, 3, 4, 5].map((p) => <option key={p} value={p}>{p}. {priorityLabels[p]}</option>)}
            </Select>
          </Field>
        ) : null}
      </div>
    </div>
  );
}

/** «Операция · Покупка · Событие · Категория»: set by the request, a tap overrides it */
export function TargetChips({ value, onChange }: { value: Target; onChange: (t: Target) => void }) {
  const labels: Record<Target, string> = { operation: 'Операция', wishlist: 'Покупка', event: 'Событие', category: 'Категория' };
  return (
    <div className="qa-lines">
      <span className="qa-lines-label">Добавить</span>
      <div className="chips">
        {(Object.keys(labels) as Target[]).map((t) => (
          <button key={t} type="button" className={cx('chip-btn', value === t && 'on')} aria-pressed={value === t} onClick={() => onChange(t)}>{labels[t]}</button>
        ))}
      </div>
    </div>
  );
}

/**
 * A phrase typed or dictated into the operation sheet. Every pause sends the whole phrase to the
 * local parser; the sheet decides which of its fields the answer changes.
 */
export function PhraseInput({ onDraft, onClear, hint }: { onDraft: (d: AssistantDraft, fresh?: boolean) => void; onClear?: () => void; hint?: string | null }) {
  const mobile = useIsMobile();
  const [text, setText] = useState('');
  const [listening, setListening] = useState(false);
  const [state, setState] = useState<{ busy?: boolean; error?: string }>({});
  const field = useRef<HTMLTextAreaElement>(null);
  const stop = useRef<(() => void) | null>(null);
  const request = useRef<AbortController | null>(null);
  const seq = useRef(0);
  const take = useRef(0); // dictation results of an earlier take (before «clear») are dropped
  const sent = useRef('');
  const draftTo = useRef(onDraft);
  draftTo.current = onDraft;

  /** `fresh`: the same phrase again, and the form is filled from it anew */
  const send = async (value: string, fresh = false) => {
    const phrase = value.replace(/\s+/g, ' ').trim();
    if (!phrase || (phrase === sent.current && !fresh)) return;
    if (phrase.length > MAX) { setState({ error: `Фраза длиннее ${MAX} знаков — сократите её` }); return; }
    if (!navigator.onLine) { setState({ error: 'Без сети фраза не распознаётся — заполните поля вручную' }); return; }
    sent.current = phrase;
    request.current?.abort();
    const ctrl = new AbortController();
    request.current = ctrl;
    const n = ++seq.current;
    setState({ busy: true });
    try {
      const draft = await api<AssistantDraft>('/assistant/parse', { method: 'POST', body: { text: phrase }, signal: ctrl.signal });
      if (n !== seq.current) return;
      setState({});
      draftTo.current(draft, fresh);
    } catch (e) {
      if (n !== seq.current || ctrl.signal.aborted) return;
      sent.current = '';
      const status = e instanceof ApiError ? e.status : 0;
      // too many requests while dictating: the next pause sends the phrase again
      setState(status === 429 ? {} : { error: status === 503 ? 'Распознавание сейчас недоступно — заполните поля вручную' : (e as Error).message });
    }
  };

  // a pause in typing or speech sends the phrase
  useEffect(() => {
    const t = window.setTimeout(() => void send(text), 400);
    return () => window.clearTimeout(t);
  }, [text]);

  useEffect(() => () => { stop.current?.(); request.current?.abort(); }, []);

  const toggleMic = () => {
    if (listening) { stop.current?.(); return; }
    if (!speechSupported()) {
      setState({ error: 'Голосовой ввод в этом браузере недоступен — продиктуйте через микрофон на клавиатуре' });
      field.current?.focus();
      return;
    }
    const before = text.trim();
    const mine = take.current;
    setState({});
    setListening(true);
    stop.current = listen(
      (heard, final) => {
        if (mine !== take.current) return;
        const value = before ? `${before} ${heard}` : heard;
        setText(value);
        if (final) void send(value);
      },
      (error) => { setListening(false); stop.current = null; if (error) setState({ error }); },
    );
  };

  // a new phrase from scratch: the fields already filled stay, the next phrase sets them again
  const clear = (fromKeyboard: boolean) => {
    take.current++;
    stop.current?.();
    request.current?.abort();
    seq.current++;
    sent.current = '';
    setText('');
    setState({});
    onClear?.();
    if (fromKeyboard) field.current?.focus();
  };

  const again = () => {
    if (listening) stop.current?.();
    void send(text, true);
  };

  const status = state.error ?? (listening ? 'Слушаю…' : state.busy ? 'Распознаю…' : hint ?? ' ');
  return (
    <div className="phrase">
      <div className="phrase-row">
        <div className="phrase-field">
          <textarea ref={field} className="input phrase-input" rows={2} value={text} maxLength={MAX + 200}
            placeholder="Например: продукты 1250 со сбера вчера" aria-label="Операция словами"
            autoFocus={!mobile} data-autofocus={!mobile || undefined}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(text); } }} />
          {/* mousedown keeps the focus (and the phone keyboard) where it was */}
          <button type="button" className={cx('icon-btn phrase-clear', text && 'shown')} aria-label="Очистить фразу"
            tabIndex={text ? 0 : -1} aria-hidden={!text || undefined}
            onMouseDown={(e) => e.preventDefault()} onClick={(e) => clear(e.detail === 0)}>
            <Icon name="close" size={18} />
          </button>
        </div>
        <div className="phrase-actions">
          <button type="button" className={cx('icon-btn phrase-mic', listening && 'on')} aria-pressed={listening}
            aria-label={listening ? 'Остановить диктовку' : 'Диктовать'} onClick={toggleMic}>
            <span className="swap" key={String(listening)}><Icon name={listening ? 'stop' : 'mic'} size={22} /></span>
          </button>
          {/* the phrase as it is now, read again: every field it names is filled anew, the rest goes back to the defaults */}
          <button type="button" className={cx('icon-btn phrase-again', state.busy && 'busy')} aria-label="Распознать заново"
            title="Распознать заново" disabled={!text.trim()} onMouseDown={(e) => e.preventDefault()} onClick={again}>
            <Icon name="redo" size={20} />
          </button>
        </div>
      </div>
      <p className={cx('phrase-status', state.error && 'neg')} aria-live="polite">{status}</p>
    </div>
  );
}

const problemText: Record<string, string> = {
  wallet_not_understood: 'Кошелёк не распознан — выберите его',
  unknown_wallet: 'Такого кошелька нет — выберите его',
  ambiguous_wallet: 'Под это название подходит несколько кошельков — выберите нужный',
  archived_wallet: 'Этот кошелёк в архиве — выберите другой',
  date_not_understood: 'Дата не распознана — выберите её',
  future_date: 'Дата в будущем: операции записываются по факту',
  amount_not_understood: 'Сумма не распознана — введите её',
  currency_mismatch: 'Валюта не совпадает с кошельком',
  wallet_empty: 'На кошельке нет денег для перевода',
  debt_not_found: 'Не нашёл открытый долг — выберите его',
  no_open_debt: 'У этого контакта нет открытого долга — выберите долг',
  unknown_contact: 'Такого контакта нет — выберите или введите имя',
  ambiguous_contact: 'Под это имя подходит несколько контактов — выберите нужный',
  fee_not_understood: 'Комиссия не распознана — введите её',
  amount_to_not_understood: 'Сумма зачисления не распознана — введите её',
};
// a purchase, an event or a category switches the sheet to its own fields (targetOf): no hint needed
const intentText: Partial<Record<AssistantDraft['intent'], string>> = {
  multiple: 'Похоже на несколько операций — продиктуйте их по одной',
  unsupported: 'Не понял фразу — скажите сумму и на что',
};

/** the one line under the phrase: why a field stayed empty, or that the phrase is not an operation */
export function draftHint(d: AssistantDraft | null): string | null {
  if (!d) return null;
  if (!d.mode) return intentText[d.intent] ?? null;
  const problem = d.problems.find((p) => problemText[p]);
  if (problem) return problemText[problem];
  if (d.amountAll && d.amount) return d.mode === 'debt' ? 'Сумма — весь остаток долга' : 'Сумма — весь остаток кошелька';
  return null;
}
